import React from "react";
import { ShieldCheck } from "lucide-react";
import { useLang } from "@/context/LangContext";

const BLURB_TR = {
  sk: "Tento používateľ je oficiálne certifikovaný značkou SauberEko® (registrovaná ochranná známka EUIPO) za dlhoročnú profesionalitu a kvalitu prevedených prác.",
  en: "This user is officially certified by SauberEko® (a trademark registered with EUIPO) for years of professionalism and quality workmanship.",
  cs: "Tento uživatel je oficiálně certifikován značkou SauberEko® (registrovaná ochranná známka EUIPO) za dlouholetou profesionalitu a kvalitu provedených prací.",
  de: "Dieser Nutzer ist offiziell durch SauberEko® zertifiziert (eingetragene EUIPO-Marke) — für jahrelange Professionalität und Qualität der ausgeführten Arbeiten.",
  pl: "Ten użytkownik jest oficjalnie certyfikowany przez SauberEko® (znak towarowy zarejestrowany w EUIPO) za wieloletnią profesjonalność i jakość wykonanych prac.",
};

/**
 * SauberEko® Certified Professional badge.
 * Sizes: "xs" (chat avatar overlay), "sm" (next to name), "md" (profile card), "lg" (hero).
 */
export const CertifiedBadge = ({ size = "sm", certificateNo, showLabel = false, className = "" }) => {
  const { lang } = useLang() || { lang: "sk" };
  const blurb = BLURB_TR[lang] || BLURB_TR.sk;
  const tooltip = certificateNo
    ? `SauberEko® Certified Professional · ${certificateNo}`
    : "SauberEko® Certified Professional";

  if (size === "xs") {
    // Tiny corner overlay for chat avatars
    return (
      <span
        title={tooltip}
        data-testid="certified-badge-xs"
        className={`inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-gradient-to-br from-yellow-400 to-amber-600 ring-1 ring-[var(--wb-bg)] shadow-sm ${className}`}
      >
        <ShieldCheck className="w-2.5 h-2.5 text-black" strokeWidth={3} />
      </span>
    );
  }

  if (size === "sm") {
    return (
      <span
        title={tooltip}
        data-testid="certified-badge-sm"
        className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-mono font-bold uppercase tracking-wider bg-gradient-to-br from-yellow-400 to-amber-600 text-black border border-yellow-300/40 shadow-sm rounded-sm ${className}`}
      >
        <ShieldCheck className="w-2.5 h-2.5" strokeWidth={3} />
        {showLabel ? "CERTIFIED" : ""}
      </span>
    );
  }

  if (size === "md") {
    return (
      <div
        data-testid="certified-badge-md"
        className={`inline-flex items-center gap-2.5 px-3 py-2 bg-gradient-to-r from-yellow-500/15 via-amber-500/10 to-yellow-600/15 border border-yellow-500/40 rounded-sm ${className}`}
      >
        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-yellow-400 to-amber-600 shadow-md shrink-0">
          <ShieldCheck className="w-4 h-4 text-black" strokeWidth={3} />
        </div>
        <div className="leading-tight">
          <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-yellow-400">
            SauberEko® Certified
          </div>
          <div className="text-xs font-display font-bold text-white">
            Professional & Expertise
          </div>
          {certificateNo && (
            <div className="text-[9px] font-mono text-zinc-500 mt-0.5">{certificateNo}</div>
          )}
        </div>
      </div>
    );
  }

  // lg — full prestige card
  return (
    <div
      data-testid="certified-badge-lg"
      className={`relative overflow-hidden bg-gradient-to-br from-[#1a1408] via-[#2a1f0a] to-[#0f0a04] border border-yellow-500/60 p-5 shadow-2xl ${className}`}
    >
      <div className="absolute -top-8 -right-8 w-32 h-32 bg-yellow-500/20 blur-3xl rounded-full" />
      <div className="relative flex items-start gap-4">
        <div className="flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-yellow-300 via-yellow-500 to-amber-700 shadow-xl shrink-0 ring-2 ring-yellow-400/40">
          <ShieldCheck className="w-7 h-7 text-black" strokeWidth={3} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-yellow-400 mb-1">
            SauberEko® Certificate
          </div>
          <div className="font-display font-black text-xl text-white leading-tight">
            Professionalism & Expertise
          </div>
          {certificateNo && (
            <div className="text-xs font-mono text-zinc-400 mt-2">№ {certificateNo}</div>
          )}
          <div className="text-[10px] text-zinc-500 mt-3 leading-relaxed">
            {blurb}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CertifiedBadge;
